/** Directed graph with non-negative edge weights; supports multi-edges (weights sum). */
export class DiGraph {
  private readonly out = new Map<string, Map<string, number>>();
  private readonly in_ = new Map<string, Map<string, number>>();
  private readonly _nodes = new Set<string>();

  addNode(v: string): void {
    this._nodes.add(v);
  }

  hasNode(v: string): boolean {
    return this._nodes.has(v);
  }

  addEdge(u: string, v: string, w = 1): void {
    if (w < 0) throw new Error("edge weight must be non-negative");
    this._nodes.add(u);
    this._nodes.add(v);
    const o = this.out.get(u) ?? new Map();
    o.set(v, (o.get(v) ?? 0) + w);
    this.out.set(u, o);
    const i = this.in_.get(v) ?? new Map();
    i.set(u, (i.get(u) ?? 0) + w);
    this.in_.set(v, i);
  }

  get nodes(): string[] {
    return [...this._nodes].sort();
  }

  get nodeSet(): Set<string> {
    return new Set(this._nodes);
  }

  outWeight(u: string): number {
    const m = this.out.get(u);
    if (!m) return 0;
    let s = 0;
    for (const w of m.values()) s += w;
    return s;
  }

  inWeight(v: string): number {
    const m = this.in_.get(v);
    if (!m) return 0;
    let s = 0;
    for (const w of m.values()) s += w;
    return s;
  }

  /** Neighbors of u along outgoing edges (target, total weight to target). */
  outNeighbors(u: string): { v: string; w: number }[] {
    const m = this.out.get(u);
    if (!m) return [];
    return [...m.entries()].map(([v, w]) => ({ v, w }));
  }

  inNeighbors(v: string): { u: string; w: number }[] {
    const m = this.in_.get(v);
    if (!m) return [];
    return [...m.entries()].map(([u, w]) => ({ u, w }));
  }

  isDangling(v: string): boolean {
    return this.outWeight(v) === 0;
  }

  toUndirectedEdgeSet(): { u: string; v: string; w: number }[] {
    const seen = new Set<string>();
    const edges: { u: string; v: string; w: number }[] = [];
    for (const u of this._nodes) {
      for (const { v, w } of this.outNeighbors(u)) {
        const a = u < v ? u : v;
        const b = u < v ? v : u;
        const key = `${a}\0${b}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const rev = this.out.get(v)?.get(u) ?? 0;
        edges.push({ u: a, v: b, w: w + rev });
      }
    }
    return edges;
  }
}
